import { useState } from 'react'
import { router, Link } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { AdminLayout } from '@/layouts/admin-layout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { MultiSelectFilter } from '@/components/ui/multi-select-filter'
import { StarRating } from '@/components/ui/star-rating'

interface Gotcha {
  id: number
  name: string
  description: string
  domain: string
  tags: string[]
  keywords: string[]
  trigger_tool: string | null
  trigger_file_pattern: string | null
  trigger_content: string[]
  trigger_content_mode: string | null
  check_type: string | null
  related_manuals: string[]
  active: boolean
  rejection_reason: string | null
  reviewed_by: string | null
  reviewed_at: string | null
  trigger_count: number
  injection_count: number
  search_count: number
  average_rating: number
  reviews_count: number
  needs_attention: boolean
  message_id: number | null
  dev_instance_id: number | null
  created_at: string
}

interface Pagination {
  page: number
  count: number
  pages: number
  last: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Props {
  gotchas: Gotcha[]
  pagy: Pagination
  filters: {
    active: string
    name_or_description_cont?: string
    s?: string
    domain_in?: string[]
    trigger_tool_in?: string[]
    check_type_in?: string[]
  }
  valid_domains: string[]
  valid_trigger_tools: string[]
  valid_check_types: string[]
  stats: { active: number; rejected: number; total: number; needs_attention: number }
}

function Index({ gotchas, pagy, filters, stats, valid_domains, valid_trigger_tools, valid_check_types }: Props) {
  const { t } = useTranslation('admin')
  const [searchQuery, setSearchQuery] = useState(filters.name_or_description_cont || '')
  const [selectedDomains, setSelectedDomains] = useState<string[]>(filters.domain_in || [])
  const [selectedTriggerTools, setSelectedTriggerTools] = useState<string[]>(filters.trigger_tool_in || [])
  const [selectedCheckTypes, setSelectedCheckTypes] = useState<string[]>(filters.check_type_in || [])

  const basePath = '/admin/collective_knowledge/gotchas'

  const getStatusBadge = (active: boolean) => (
    <Badge variant={active ? 'success' : 'destructive'}>
      {active ? t('collectiveKnowledge.gotchas.status.active') : t('collectiveKnowledge.gotchas.status.rejected')}
    </Badge>
  )

  const buildParams = (overrides: Record<string, string | string[] | null> = {}) => {
    const params: Record<string, string | string[]> = { active: filters.active || 'true' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    if (selectedDomains.length > 0) params['q[domain_in][]'] = selectedDomains
    if (selectedTriggerTools.length > 0) params['q[trigger_tool_in][]'] = selectedTriggerTools
    if (selectedCheckTypes.length > 0) params['q[check_type_in][]'] = selectedCheckTypes

    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort

    for (const [key, value] of Object.entries(overrides)) {
      if (value === null || value === '') {
        delete params[key]
      } else {
        params[key] = value
      }
    }
    return params
  }

  const handleStatusChange = (value: string) => {
    router.get(basePath, { ...buildParams(), active: value }, { preserveState: true })
  }

  const handleSearch = () => {
    router.get(basePath, buildParams(), { preserveState: true })
  }

  const handleReset = () => {
    setSearchQuery('')
    setSelectedDomains([])
    setSelectedTriggerTools([])
    setSelectedCheckTypes([])
    router.get(basePath, {}, { preserveState: true })
  }

  const handleFilterChange = (
    setter: (v: string[]) => void,
    paramKey: string,
    values: string[],
    otherFilters: Record<string, string[]>
  ) => {
    setter(values)
    const params: Record<string, string | string[]> = { active: filters.active || 'true' }
    if (searchQuery) params['q[name_or_description_cont]'] = searchQuery
    if (values.length > 0) params[paramKey] = values
    for (const [key, vals] of Object.entries(otherFilters)) {
      if (vals.length > 0) params[key] = vals
    }
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')
    if (currentSort) params['q[s]'] = currentSort
    router.get(basePath, params, { preserveState: true })
  }

  const handleSort = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')

    let newSort = `${field} asc`
    if (currentSort === `${field} asc`) {
      newSort = `${field} desc`
    }

    router.get(basePath, { ...buildParams(), 'q[s]': newSort }, { preserveState: true })
  }

  const getSortIcon = (field: string) => {
    const urlParams = new URLSearchParams(window.location.search)
    const currentSort = urlParams.get('q[s]')

    if (currentSort === `${field} asc`) return '↑'
    if (currentSort === `${field} desc`) return '↓'
    return '⇅'
  }

  return (
    <div className="flex flex-1 flex-col">
      <div className="@container/main flex flex-1 flex-col gap-2">
        <div className="flex flex-col py-2">
          <div className="px-3">
            {/* Filter Bar */}
            <div className="flex items-center gap-2 mb-2 flex-wrap">
              <span className="text-sm font-semibold whitespace-nowrap">
                {t('collectiveKnowledge.gotchas.title')} ({pagy.count})
              </span>
              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                <Badge variant="success" className="text-[10px] px-1 py-0">{stats.active}</Badge>
                <span>/</span>
                <Badge variant="destructive" className="text-[10px] px-1 py-0">{stats.rejected}</Badge>
                {stats.needs_attention > 0 && (
                  <>
                    <span>/</span>
                    <Badge variant="warning" className="text-[10px] px-1 py-0">{stats.needs_attention} {t('collectiveKnowledge.gotchas.stats.needsAttention')}</Badge>
                  </>
                )}
              </div>
              <div className="w-px h-4 bg-border" />
              <Select
                value={filters.active || 'true'}
                onValueChange={handleStatusChange}
              >
                <SelectTrigger className="h-7 text-xs w-28">
                  <SelectValue placeholder={t('collectiveKnowledge.gotchas.filters.status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('collectiveKnowledge.gotchas.filters.all')}</SelectItem>
                  <SelectItem value="true">{t('collectiveKnowledge.gotchas.status.active')}</SelectItem>
                  <SelectItem value="false">{t('collectiveKnowledge.gotchas.status.rejected')}</SelectItem>
                </SelectContent>
              </Select>
              <MultiSelectFilter
                label={t('collectiveKnowledge.gotchas.filters.domain')}
                options={valid_domains}
                selected={selectedDomains}
                onChange={(v) => handleFilterChange(
                  setSelectedDomains, 'q[domain_in][]', v,
                  { 'q[trigger_tool_in][]': selectedTriggerTools, 'q[check_type_in][]': selectedCheckTypes }
                )}
              />
              <MultiSelectFilter
                label={t('collectiveKnowledge.gotchas.filters.trigger')}
                options={valid_trigger_tools}
                selected={selectedTriggerTools}
                onChange={(v) => handleFilterChange(
                  setSelectedTriggerTools, 'q[trigger_tool_in][]', v,
                  { 'q[domain_in][]': selectedDomains, 'q[check_type_in][]': selectedCheckTypes }
                )}
              />
              <MultiSelectFilter
                label={t('collectiveKnowledge.gotchas.filters.check')}
                options={valid_check_types}
                selected={selectedCheckTypes}
                onChange={(v) => handleFilterChange(
                  setSelectedCheckTypes, 'q[check_type_in][]', v,
                  { 'q[domain_in][]': selectedDomains, 'q[trigger_tool_in][]': selectedTriggerTools }
                )}
              />
              <div className="w-px h-4 bg-border" />
              <Input
                type="text"
                placeholder={t('collectiveKnowledge.gotchas.filters.searchPlaceholder')}
                className="h-7 text-xs w-48"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
              <Button onClick={handleSearch} className="h-7 px-2 text-xs">
                {t('collectiveKnowledge.gotchas.buttons.search')}
              </Button>
              <Button onClick={handleReset} variant="ghost" className="h-7 px-2 text-xs">
                {t('collectiveKnowledge.gotchas.buttons.reset')}
              </Button>
            </div>

            {/* Gotchas Table */}
            <div className="bg-card rounded-lg border border-border overflow-hidden">
              <div className="overflow-y-auto" style={{ maxHeight: 'calc(100vh - 180px)', minHeight: '400px' }}>
                <table className="w-full">
                  <thead className="bg-muted sticky top-0 z-10">
                    <tr>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.gotchas.table.name')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.gotchas.table.domain')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.gotchas.table.trigger')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.gotchas.table.check')}
                      </th>
                      <th className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.gotchas.table.status')}
                      </th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('trigger_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {t('collectiveKnowledge.gotchas.table.triggers')}
                          <span className="text-gray-400">{getSortIcon('trigger_count')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('search_count')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {t('collectiveKnowledge.gotchas.table.searches')}
                          <span className="text-gray-400">{getSortIcon('search_count')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('average_rating')}
                      >
                        <div className="flex items-center justify-end gap-1">
                          {t('collectiveKnowledge.gotchas.table.rating')}
                          <span className="text-gray-400">{getSortIcon('average_rating')}</span>
                        </div>
                      </th>
                      <th
                        className="px-3 py-1.5 text-left text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/80 select-none"
                        onClick={() => handleSort('created_at')}
                      >
                        <div className="flex items-center gap-1">
                          {t('collectiveKnowledge.gotchas.table.created')}
                          <span className="text-gray-400">{getSortIcon('created_at')}</span>
                        </div>
                      </th>
                      <th className="px-3 py-1.5 text-right text-xs font-medium text-muted-foreground">
                        {t('collectiveKnowledge.gotchas.table.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-card divide-y divide-border">
                    {gotchas.length > 0 ? (
                      gotchas.map((gotcha) => (
                        <tr key={gotcha.id} className="hover:bg-muted transition-colors">
                          <td className="px-3 py-1.5">
                            <div className="flex flex-col gap-0.5">
                              <span className="text-sm font-medium text-foreground">{gotcha.name}</span>
                              <span className="text-xs text-muted-foreground truncate max-w-md">{gotcha.description}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <Badge variant="secondary" className="text-xs">{gotcha.domain}</Badge>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {gotcha.trigger_tool ? (
                              <Badge variant="secondary" className="text-xs">{gotcha.trigger_tool}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {gotcha.check_type ? (
                              <Badge variant="secondary" className="text-xs">{gotcha.check_type}</Badge>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            {getStatusBadge(gotcha.active)}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <div className="flex flex-col items-end">
                              <span className="text-sm text-foreground">{gotcha.trigger_count}</span>
                              <span className="text-[10px] text-muted-foreground">{gotcha.injection_count} {t('collectiveKnowledge.gotchas.table.injected')}</span>
                            </div>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <span className="text-sm text-foreground">{gotcha.search_count}</span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            {gotcha.reviews_count > 0 ? (
                              <div className="flex items-center justify-end gap-1.5">
                                <StarRating value={gotcha.average_rating} />
                                <span className="text-xs text-muted-foreground">({gotcha.reviews_count})</span>
                                {gotcha.needs_attention && (
                                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-500" title={t('collectiveKnowledge.gotchas.stats.needsAttention')} />
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-muted-foreground">-</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap">
                            <span className="text-sm text-foreground">
                              {new Date(gotcha.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                            </span>
                          </td>
                          <td className="px-3 py-1.5 whitespace-nowrap text-right">
                            <Link
                              href={`${basePath}/${gotcha.id}`}
                              className="text-sm text-blue-600 hover:text-blue-900"
                            >
                              {t('collectiveKnowledge.gotchas.buttons.view')}
                            </Link>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={10} className="px-3 py-8 text-center text-sm text-muted-foreground">
                          {t('collectiveKnowledge.gotchas.empty')}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagy.pages > 1 && (
                <div className="px-3 py-1.5 flex items-center justify-between border-t border-border bg-card text-xs">
                  <span className="text-muted-foreground">
                    {pagy.from}-{pagy.to} of {pagy.count}
                  </span>
                  <div className="flex gap-1">
                    {pagy.prev && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.prev }, { preserveState: true })}
                      >
                        {t('collectiveKnowledge.gotchas.pagination.previous')}
                      </Button>
                    )}
                    {pagy.next && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 px-2 text-xs"
                        onClick={() => router.get(basePath, { ...buildParams(), page: pagy.next }, { preserveState: true })}
                      >
                        {t('collectiveKnowledge.gotchas.pagination.next')}
                      </Button>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

Index.layout = (page: React.ReactNode) => <AdminLayout>{page}</AdminLayout>

export default Index
